import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import { faComputer, faFileSpreadsheet, faPlus } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, message, Popconfirm, Skeleton, Table, Tag, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomButton from "../common/CustomButton";
import AddEditFormComponent from "../components/AddEditFormComponent";
import FilterDropdown from "../components/FilterDropDown";
import ImportDrawer from "../components/ImportDrawer";
import SearchComponent from "../components/SearchComponent";
import StockService from "../services/Stock/StockService";

const StockPage = () => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [editingStock, setEditingStock] = useState(null);
  const [openImport, setOpenImport] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [list, setList] = useState({
    request: {
      sku: null,
      description: null,
      qoh: null,
      palletQty: null,
      packSize: null,
      dimensionHeightCm: null,
      dimensionWidthCm: null,
      dimensionLengthCm: null,
      dimensionWeightKg: null,
      pageFilter: { all: false, size: 10, num: 0 },
      sortFilter: { field: "", order: "" },
    },
    response: { total: 0, data: [] },
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchStocks(list.request);
  }, []);

  const handleAddStock = (values) => {
    if (type === "edit") {
      setDataSource((prevData) =>
        prevData.data.map((item) =>
          item.key === editingStock.key ? { ...item, ...values } : item
        )
      );
    } else {
      const newStock = { key: Date.now(), ...values };
      setDataSource((prevDataSource) => ({
        ...prevDataSource,
        data: [...prevDataSource.data, newStock],
        total: prevDataSource.total + 1,
      }));
    }
    setOpen(false);
  };

  const handleEdit = (record) => {
    setEditingStock(record);
    setType("edit");
    setOpen(true);
  };

  const handleViewStock = (record) => {
    navigate("/stock-detail", {
      state: { supplierID: record.id },
    });
  };

  const exportToExcel = (request) => {
    StockService.exportToExcel(request)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        const contentDisposition = response.headers["content-disposition"];
        const filename =
          contentDisposition?.match(/filename="(.+)"/)?.[1] || "stock.xlsx";
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success("Excel file downloaded successfully.");
      })
      .catch((error) => {
        console.error("Error exporting to Excel:", error);
        message.error("Failed to download Excel file.");
      });
  };

  const handleTableChange = (pagination, sorter) => {
    const updatedRequest = {
      ...list.request,
      sortFilter: {
        field: sorter.field || "",
        order:
          sorter.order === "ascend"
            ? "asc"
            : sorter.order === "descend"
              ? "desc"
              : "",
      },
      pageFilter: {
        ...list.request.pageFilter,
        size: pagination.pageSize,
        num: pagination.current - 1,
      },
    };

    setList((prevState) => ({
      ...prevState,
      request: updatedRequest,
    }));
    fetchStocks(updatedRequest);
  };

  const handleSearch = (value) => {
    const updatedRequest = { ...list.request, sku: value };
    setList((prevState) => ({
      ...prevState,
      request: updatedRequest,
    }));
    fetchStocks(updatedRequest);
  };

  const handleStatusChange = (newStatus, record) => {
    if (record) {
      const request = { ...record, isActive: newStatus };
      StockService.updateStock(request);
      message.success(
        `Status has been updated to ${newStatus ? "Active" : "Inactive"}`
      );
      fetchStocks(list.request);
    }
  };

  const handleApplyFilters = (values) => {
    const updatedRequest = {
      ...list.request,
      sku: values.sku || null,
      description: values.description || null,
      packSize: values.packSize || null,
      qoh: values.qoh || null,
      palletQty: values.palletQty || null,
      dimensionHeightCm: values.dimensionHeightCm || null,
      dimensionLengthCm: values.dimensionLengthCm || null,
      dimensionWeightKg: values.dimensionWeightKg || null,
      dimensionWidthCm: values.dimensionWidthCm || null,
    };

    setList((prevState) => ({
      ...prevState,
      request: updatedRequest,
    }));

    fetchStocks(updatedRequest);
  };

  const fetchStocks = (request) => {
    setIsLoading(true);
    StockService.getAllStocks(request)
      .then((response) => {
        if (response.status === 200) {
          setDataSource(response.data);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching suppliers:", error);
      });
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const getRowSelection = () => ({
    selectedRowKeys,
    onChange: onSelectChange,
  });

  const columns = [
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
      align: "center",
      sorter: true,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      align: "center",
      sorter: true,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "QOH",
      dataIndex: "qoh",
      key: "qoh",
      align: "center",
      sorter: true,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "PackSize",
      dataIndex: "packSize",
      key: "packSize",
      align: "center",
      sorter: true,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "PalletQty",
      dataIndex: "palletQty",
      key: "palletQty",
      align: "center",
      sorter: true,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "DimensionHeightCm",
      dataIndex: "dimensionHeightCm",
      key: "dimensionHeightCm",
      align: "center",
      sorter: true,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "DimensionLengthCm",
      dataIndex: "dimensionLengthCm",
      key: "dimensionLengthCm",
      align: "center",
      sorter: true,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "DimensionWeightKg",
      dataIndex: "dimensionWeightKg",
      key: "dimensionWeightKg",
      align: "center",
      sorter: true,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "DimensionWidthCm",
      dataIndex: "dimensionWidthCm",
      key: "dimensionWidthCm",
      align: "center",
      sorter: true,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "status",
      render: (status, record) => {
        if (isLoading) {
          return <Skeleton active size="small" />;
        }
        return (
          <Popconfirm
            title={`Are you sure you want to change the status to ${status ? "Inactive" : "Active"
              }?`}
            onConfirm={() => handleStatusChange(!status, record)}
            okText="Yes"
            cancelText="No"
          >
            <Tag
              className={`${status
                ? "text-primary border-primary hover:cursor-pointer hover:bg-primary hover:text-white"
                : "text-red-500 border-red-500 hover:cursor-pointer hover:bg-red hover:!text-white"
                }`}
            >
              {status ? "Active" : "Inactive"}
            </Tag>
          </Popconfirm>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (text, record) => {
        if (isLoading) {
          return <Skeleton active size="small" />;
        }
        return (
          <div className="flex justify-center space-x-4">
            <Tooltip title="Edit">
              <Button onClick={() => handleEdit(record)}>
                <EditOutlined />
              </Button>
            </Tooltip>
            <Tooltip title="View">
              <Button onClick={() => handleViewStock(record)}>
                <EyeOutlined />
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-3">
      <div className="flex mb-2">
        <div className="flex justify-between items-center w-1/2 gap-x-4">
          <FilterDropdown onApplyFilters={handleApplyFilters} type="stock-page" />
          <SearchComponent type="Stock" className="!bg-gray-300" onSearch={handleSearch} />
        </div>
        <div className="flex justify-end items-center w-1/2 gap-x-4">
          <CustomButton
            className="flex items-center ml-4 rounded-lg"
            type="primary"
            onClick={() => {
              setOpen(true);
              setType("add");
              setEditingStock(null);
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add
          </CustomButton>
          <CustomButton
            type="secondary"
            className="flex items-center rounded-lg"
            onClick={() => setOpenImport(true)}
          >
            <FontAwesomeIcon icon={faComputer} />
            Import
          </CustomButton>
          <CustomButton
            className="flex items-center rounded-lg"
            type="tertiary"
            onClick={() => exportToExcel(list.request)}
          >
            <FontAwesomeIcon icon={faFileSpreadsheet} />
            Export
          </CustomButton>
        </div>
      </div>
      {/* <Table
        className="mx-0 border-2 border-gray-300 rounded-lg overflow-x-auto"
        rowSelection={getRowSelection()}
        columns={columns}
        dataSource={
          isLoading
            ? new Array(10).fill(null).map((_, index) => ({ key: index }))
            : dataSource?.data?.map((item, index) => ({
                ...item,
                key: index,
              }))
        }
        pagination={{
          defaultPageSize: 10,
          pageSizeOptions: ["5", "10", "20", "50", "75", "100"],
          total: dataSource.total,
          showSizeChanger: true,
          showQuickJumper: true,
        }}
        onChange={handleTableChange}
        bordered={false}
        rowClassName="shadow-lg"
        scroll={{ x: "max-content" }}
      /> */}
      <Table
        className="mx-0 border-2 border-gray-300 rounded-lg overflow-x-auto "
        rowSelection={getRowSelection()}
        columns={columns}
        dataSource={
          isLoading
            ? new Array(10).fill(null).map((_, index) => ({ key: index }))
            : dataSource?.data?.map((item, index) => ({
              ...item,
              key: index,
            }))
        }
        pagination={{
          defaultPageSize: 10,
          pageSizeOptions: ["5", "10", "20", "50", "75", "100"],
          total: dataSource.total,
          showSizeChanger: true,
          showQuickJumper: true,
        }}
        onChange={handleTableChange}
        bordered={false}
        rowClassName="shadow-lg"
        scroll={{ x: "max-content" }}
      />

      <AddEditFormComponent
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleAddStock}
        initialValues={editingStock}
        type={type}
        onSuccess={() => {
          const { request } = list;
          fetchStocks(request);
        }}
        formType="stock"
      />

      <ImportDrawer
        open={openImport}
        onClose={() => setOpenImport(false)}
        type="StockImport"
      />
    </div>
  );
};

export default StockPage;
